import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  cancelAction,
  confirmationDialogData,
  ConfirmDialogComponent,
  DialogPresets,
  toSerialisedEntityModel$,
  type IConfirmationDialogData,
  type IConfirmationDialogInput,
} from '@principle-theorem/ng-shared';
import {
  AutomatedNotification,
  type AutomationEntity,
} from '@principle-theorem/principle-core';
import {
  AutomationContext,
  AutomationStatus,
  type IAppointmentScopeData,
  type IAutomation,
  type IPractice,
  type TreatmentStepAutomation,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  isReffable,
  patchDoc,
  serialise$,
  toEntityModel,
  unserialise$,
  type DocumentReference,
  type Reffable,
} from '@principle-theorem/shared';
import {
  combineLatest,
  from,
  of,
  type Observable,
  type OperatorFunction,
} from 'rxjs';
import {
  concatMap,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { IAutomatedNotificationDialogData } from '../../components/notification-dialog/notification-dialog.component';
import { AutomationDialogService } from '../../services/automation-dialog.service';
import { AutomationActions, AutomationListActions } from '../actions';
import { AutomationsFacade } from '../facades/automations.facade';

@Injectable()
export class AutomationsListEffects {
  private _actions$ = inject(Actions);
  private _automationsFacade = inject(AutomationsFacade);
  addTask$: Observable<Action> = createEffect(() => this._addTask$());
  editTask$: Observable<Action> = createEffect(() => this._editTask$());
  addNotification$: Observable<Action> = createEffect(() =>
    this._addNotification$()
  );
  editNotification$: Observable<Action> = createEffect(() =>
    this._editNotification$()
  );
  addAutomatedFormIssue$: Observable<Action> = createEffect(() =>
    this._addAutomatedFormIssue$()
  );
  editAutomatedFormIssue$: Observable<Action> = createEffect(() =>
    this._editAutomatedFormIssue$()
  );
  addFromTemplate$: Observable<Action> = createEffect(() =>
    this._addFromTemplate$()
  );
  confirmCancellation$: Observable<Action> = createEffect(() =>
    this._confirmCancellation$()
  );
  cancelSelected$: Observable<Action> = createEffect(() =>
    this._cancelSelected$()
  );

  constructor(
    private _dialog: MatDialog,
    private _automationDialogService: AutomationDialogService,
    private _snackBar: MatSnackBar
  ) {}

  private _addTask$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.addGeneratedTask),
      unserialise$(),
      mergeMap(({ event, scopeData, stepRef, triggerAfterDate, context }) =>
        from(
          this._automationDialogService.openTaskAdd(
            event,
            scopeData,
            context,
            triggerAfterDate
          )
        ).pipe(
          filterUndefined(),
          map((automation) => ({
            automation: toEntityModel(automation),
            stepRef,
          })),
          serialise$()
        )
      ),
      map(({ automation, stepRef }) => {
        if (!automation) {
          return cancelAction();
        }
        return AutomationActions.addAutomation({
          automation,
          stepRef,
        });
      })
    );
  }

  private _editTask$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.editGeneratedTask),
      unserialise$(),
      switchMap(
        ({
          id,
          generatedTask,
          event,
          triggerDate,
          triggerAfterDate,
          skipEffect,
          scopeData,
          context,
        }) =>
          from(
            this._automationDialogService.openTaskEdit(
              generatedTask,
              event,
              scopeData,
              context,
              triggerDate,
              triggerAfterDate
            )
          ).pipe(filterUndefined(), this._dispatchUpdate(id, skipEffect))
      )
    );
  }

  private _addNotification$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.addNotification),
      unserialise$(),
      mergeMap(
        ({
          scope,
          templateScopeData,
          scopeData,
          event,
          stepRef,
          triggerAfterDate,
        }) => {
          const dialogData: IAutomatedNotificationDialogData = {
            scope,
            scopeData: templateScopeData,
            useRelativeTime: true,
            context: AutomationContext.Appointment,
          };
          return from(
            this._automationDialogService.openNotificationDialog(
              dialogData,
              event,
              scopeData,
              triggerAfterDate
            )
          ).pipe(
            filterUndefined(),
            map((automation) => ({
              automation: toEntityModel(automation),
              stepRef,
            })),
            serialise$()
          );
        }
      ),
      map(({ automation, stepRef }) => {
        if (!automation) {
          return cancelAction();
        }
        return AutomationActions.addAutomation({
          automation,
          stepRef,
        });
      })
    );
  }

  private _addAutomatedFormIssue$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.addAutomatedFormIssue),
      unserialise$(),
      mergeMap(({ event, stepRef, triggerAfterDate }) =>
        from(
          this._automationDialogService.openAutomatedFormIssueAdd(
            event,
            triggerAfterDate
          )
        ).pipe(
          filterUndefined(),
          map((automation) => ({
            automation: toEntityModel(automation),
            stepRef,
          })),
          serialise$()
        )
      ),
      map(({ automation, stepRef }) => {
        if (!automation) {
          return cancelAction();
        }
        return AutomationActions.addAutomation({
          automation,
          stepRef,
        });
      })
    );
  }

  private _editAutomatedFormIssue$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.editAutomatedFormIssue),
      unserialise$(),
      switchMap(
        ({ id, formIssue, event, triggerDate, triggerAfterDate, skipEffect }) =>
          from(
            this._automationDialogService.openAutomatedFormIssueEdit(
              formIssue,
              event,
              triggerDate,
              triggerAfterDate
            )
          ).pipe(filterUndefined(), this._dispatchUpdate(id, skipEffect))
      )
    );
  }

  private _addFromTemplate$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.addFromTemplate),
      unserialise$(),
      mergeMap((data) =>
        this._automationDialogService
          .openFromTemplateDialog$(
            data.event.practice.ref as DocumentReference<IPractice>,
            data.event,
            data.scopeData,
            data.triggerAfterDate
          )
          .pipe(
            filterUndefined(),
            map((automation) => ({
              automation: toEntityModel(automation),
              stepRef: data.stepRef,
            })),
            serialise$()
          )
      ),
      map(({ automation, stepRef }) => {
        if (!automation) {
          return cancelAction();
        }

        this._snackBar.open('Automation added successfully');
        return AutomationActions.addAutomation({
          automation,
          stepRef,
        });
      })
    );
  }

  private _editNotification$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.editNotification),
      unserialise$(),
      concatMap(
        ({
          id,
          notification,
          event,
          triggerDate,
          triggerAfterDate,
          skipEffect,
          useRelativeTime,
          configRef,
          scopeData,
          context,
        }) => {
          // TODO: Render based on actual scope data. https://app.clickup.com/t/860qzurhu
          const templateScopeData: IAppointmentScopeData =
            {} as IAppointmentScopeData;
          return from(
            AutomatedNotification.getTemplateScope(notification)
          ).pipe(
            switchMap((scope) => {
              const dialogData: IAutomatedNotificationDialogData = {
                scope,
                scopeData: templateScopeData,
                notification,
                triggerDate,
                useRelativeTime,
                configRef,
                context,
              };
              return from(
                this._automationDialogService.openNotificationDialog(
                  dialogData,
                  event,
                  scopeData,
                  triggerAfterDate
                )
              ).pipe(filterUndefined(), this._dispatchUpdate(id, skipEffect));
            })
          );
        }
      )
    );
  }

  private _confirmCancellation$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.cancelSelected),
      withLatestFrom(
        this._automationsFacade.selectedAutomations$.pipe(
          map((selected) =>
            selected.filter(
              (automation) => automation.status !== AutomationStatus.Cancelled
            )
          )
        )
      ),
      switchMap(([_, cancellable]) => {
        if (!cancellable.length) {
          this._snackBar.open('All selected automations previously cancelled');
          return of(cancelAction());
        }
        const data: IConfirmationDialogData = confirmationDialogData({
          title: 'Cancel Automations',
          prompt: `Are you sure you want to cancel selected automations?`,
        });
        return this._dialog
          .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
            ConfirmDialogComponent,
            DialogPresets.small({ data })
          )
          .afterClosed()
          .pipe(
            map((confirmed) => {
              if (!confirmed) {
                return cancelAction();
              }
              const ids = cancellable.map((automation) => automation.uid);
              return AutomationListActions.cancelSelectedConfirmed({ ids });
            })
          );
      })
    );
  }

  private _cancelSelected$(): Observable<Action> {
    return this._actions$.pipe(
      ofType(AutomationListActions.cancelSelectedConfirmed),
      withLatestFrom(this._automationsFacade.selectedAutomations$),
      concatMap(([{ ids }, automations]) => {
        const updates = automations
          .filter(
            (automation): automation is Reffable<AutomationEntity> =>
              isReffable(automation) && ids.includes(automation.uid)
          )
          .map((automation) => patchDoc(automation.ref, automation));
        return combineLatest(updates);
      }),
      map(() => AutomationListActions.unselectAll()),
      tap(() => this._snackBar.open('Automations Cancelled'))
    );
  }

  private _dispatchUpdate(
    id: string,
    skipEffect: boolean = false
  ): OperatorFunction<IAutomation<TreatmentStepAutomation>, Action> {
    return (source$) => {
      return source$.pipe(
        toSerialisedEntityModel$(),
        map((automation) => {
          if (!automation) {
            return cancelAction();
          }
          const changes = {
            data: automation.data,
            triggerDate: automation.triggerDate,
            status: automation.status,
          };
          const update = {
            id,
            changes,
          };
          return AutomationActions.updateAutomation({ update, skipEffect });
        })
      );
    };
  }
}
